/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  AvailableStation,
  FetchStationProperties,
  RequestStationInterface,
  StationFilter,
  StationInitialProperties,
  StationListingRequest,
} from '../../../interface/partner-interfaces/station-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import station from '../../../utils/api/partner/station';
// import { CONNREFUSED } from 'dns';

// a thunk to fetch stations
export const fetchApprovedStations = createAsyncThunk<
  FetchStationProperties,
  RequestStationInterface
>(
  'approved-station-management/fetchApprovedStations',
  async (data: RequestStationInterface, { dispatch }) => {
    try {
      const response = await station.fetchStations(
        removeEmptyKeys({ ...data, submissionStatus: 'approved' }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);
export const refreshApprovedStations = createAsyncThunk<
  FetchStationProperties,
  RequestStationInterface
>('approved-station-management/refreshApprovedStations', async (data: RequestStationInterface, { dispatch }) => {
  try {
    const response = await station.fetchStations(
      removeEmptyKeys({ ...data, submissionStatus: 'approved' })
    );
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchAllStation = createAsyncThunk<FetchStationProperties, StationListingRequest>(
  'approved-station-management/fetchAllStation',
  async (data: StationListingRequest, { dispatch }) => {
    try {
      const response = await station.fetchStations(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'approved-station-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await station.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch all stations
export const fetchAllStationsList = createAsyncThunk<AvailableStation[]>(
  'approved-station-management/fetchAllStationsList',
  async () => {
    const response = await station.fetchAllStations();
    return response.data;
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: StationInitialProperties = {
  stations: [],
  partnerStations: [],
  loading: false,
  selectedStationPoint: null,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const approvedStationSlice = createSlice({
  name: 'approved-station-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setApprovedStationFilterData: (
      state: StationInitialProperties,
      action: PayloadAction<StationFilter>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Update station management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Function to update single station in redux
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateSingleStation: (
      state: StationInitialProperties,
      action: PayloadAction<AvailableStation>,
    ) => {
      const updatedStation = action.payload; // station to be added at the top
      const stationList = state.stations; // Assign the existing station to another variable

      // Find the index of the updated station by ID
      const index = stationList.findIndex((eachStation) => eachStation._id === updatedStation._id);

      // If the index found, replace the entire data
      if (index !== -1) {
        stationList[index] = {
          ...updatedStation,
          id: updatedStation._id,
          amenities: updatedStation?.amenities || [],
          opensAt: ConvertUTCtoHHMM(updatedStation?.opensAt),
          closesAt: ConvertUTCtoHHMM(updatedStation?.closesAt),
        };
      }
      state.stations = stationList; // Finally update the station list
    },
    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetApprovedStationToInitialState: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchApprovedStations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchApprovedStations.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.stations = action.payload.results?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          amenities: stationData?.amenities || [],
          opensAt: ConvertUTCtoHHMM(stationData?.opensAt),
          closesAt: ConvertUTCtoHHMM(stationData?.closesAt),
        }));
      })
            .addCase(refreshApprovedStations.fulfilled, (state, action) => {
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.stations = action.payload.results?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          amenities: stationData?.amenities || [],
          opensAt: ConvertUTCtoHHMM(stationData?.opensAt),
          closesAt: ConvertUTCtoHHMM(stationData?.closesAt),
        }));
      })
      .addCase(fetchApprovedStations.rejected, (state) => {
        state.loading = false;
      })
    builder
      .addCase(fetchAllStationsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllStationsList.fulfilled, (state, action) => {
        state.loading = false;
        state.partnerStations = action.payload.map(
          (stationData: { _id: string; name: string; address: string }) => ({
            value: stationData._id,
            label: stationData.name,
            description: stationData.address,
          }),
        );
      })
      .addCase(fetchAllStationsList.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setApprovedStationFilterData,
  updateSingleStation,
  resetApprovedStationToInitialState,
  updateStationFilters,
  disableFiltersApplied,
  setSortData,
} = approvedStationSlice.actions;

// Export the reducer
export default approvedStationSlice.reducer;
